import React, { Fragment } from 'react';

// Shared rendering of a machine attribute's "property tags" (the little pills that summarise a
// record: printing type, unit count, sheet dimensions, thickness…). Extracted so the settings list
// (SettingsUserUnitPriceAttributeList) and the library picker (LibraryAttributePicker) show an
// identical summary for the same record from one implementation.

// A field can be declared more than once for the same name — e.g. a hidden twin that carries
// change-listeners alongside the visible input. For display we want the non-hidden definition so the
// value renders, not the hidden twin (which has no renderPropertyTag branch).
const pickDisplayDefinition = (matches) =>
  matches.find((a) => a.form_type !== 'hidden') || matches[0];

const findFieldDefinition = (fieldName, subFormAttributes, sectionAttributes) => {
  if (subFormAttributes) {
    const matches = subFormAttributes.filter((a) => a.name === fieldName);
    if (matches.length > 0) { return pickDisplayDefinition(matches); }
  }
  if (sectionAttributes) {
    const matches = sectionAttributes.filter((a) => a.name === fieldName);
    if (matches.length > 0) { return pickDisplayDefinition(matches); }
  }
  return null;
}

// Whether a listed property is visible for a saved record, so the summary can skip a property that is
// hidden for that particular record (e.g. a field that only applies to certain machine types).
// QuoteBuilderForm evaluates visible_if against live form state; here we only have the record's saved
// `properties`, so the supported basis is the record's own values via v(key). A condition that reaches
// for live form state or page globals is not meaningful for a static record — window and friends are
// shadowed to undefined and unknown identifiers throw, so any such condition fails open (property is
// shown), matching the form's "no condition ⇒ visible".
const isFieldVisibleForRecord = (field, properties) => {
  if (field.visible_if === undefined) { return true; }
  const v = (key) => properties[key];
  try {
    const evaluate = new Function('v', 'window', 'values', `return (${field.visible_if});`);
    return !!evaluate(v, undefined, undefined);
  } catch (error) {
    return true;
  }
}

const resolvePropertyValue = (properties, name) => {
  if (properties[name] !== undefined) { return properties[name]; }
  if (properties[name + 's'] !== undefined) { return properties[name + 's']; }
  return undefined;
}

const renderPropertyTag = (field, properties, propertyName) => {
  const value = resolvePropertyValue(properties, propertyName || field.name);

  if (field.form_type === 'checkboxes') {
    if (!value || !Array.isArray(value) || value.length === 0) { return null; }
    return (
      <Fragment key={field.name}>
        {value.map((selectedValue) => {
          const valuePair = field.values?.find((v) => v.value === selectedValue);
          const label = valuePair ? t(valuePair.label) : selectedValue;
          return <span key={selectedValue} className="attribute-tag">{label}</span>;
        })}
      </Fragment>
    );
  }

  if (field.form_type === 'boolean') {
    if (!value) { return null; }
    return (
      <span key={field.name} className="attribute-tag attribute-tag-enabled">
        {t(field.label)}
      </span>
    );
  }

  if (field.form_type === 'select') {
    if (value === undefined || value === null || value === '') { return null; }
    const valuePair = field.values?.find((v) => String(v.value) === String(value));
    const label = valuePair ? t(valuePair.label) : value;
    return <span key={field.name} className="attribute-tag">{label}</span>;
  }

  if (field.form_type === 'length' || field.form_type === 'integer' || field.form_type === 'string') {
    if (value === undefined || value === null || value === '') { return null; }
    return <span key={field.name} className="attribute-tag">{value}</span>;
  }

  return null;
}

// Builds the tag list for a record. With `listingProperties` (a whitelist of property names from the
// tab config) it renders those in order; without one it renders every field in the create sub-form.
const buildTags = ({ properties, listingProperties, subFormAttributes, sectionAttributes }) => {
  if (listingProperties) {
    return listingProperties.map((propertyName) => {
      const field = findFieldDefinition(propertyName, subFormAttributes, sectionAttributes);
      if (!field) {
        const value = properties[propertyName];
        if (value === undefined || value === null || value === '') { return null; }
        return <span key={propertyName} className="attribute-tag">{value}</span>;
      }
      if (!isFieldVisibleForRecord(field, properties)) { return null; }
      return renderPropertyTag(field, properties, propertyName);
    }).filter(Boolean);
  }

  if (!subFormAttributes) { return []; }
  return subFormAttributes.map((field) => {
    if (!isFieldVisibleForRecord(field, properties)) { return null; }
    return renderPropertyTag(field, properties);
  }).filter(Boolean);
}

// `align: 'right'` packs the chips toward the right edge (the form's edit view shows them under the
// right-aligned dropdown); the default left keeps the picker/list summary flush-left.
const AttributePropertyTags = ({ properties, listingProperties, subFormAttributes, sectionAttributes, align }) => {
  if (!properties) { return null; }

  const tags = buildTags({ properties, listingProperties, subFormAttributes, sectionAttributes });
  if (tags.length === 0) { return null; }

  const rowStyle = align === 'right' ? { justifyContent: 'flex-end' } : undefined;

  return (
    <div className="attribute-properties">
      <div className="attribute-properties-row" style={rowStyle}>
        {tags}
      </div>
    </div>
  );
}

export default AttributePropertyTags;
export { findFieldDefinition };
